import React, { useEffect, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { toastAlert } from "../../helpers/toastAlert";
import { asEntityId, formatDate } from "../../utils/commonutils";
import { getApiErrorMessage, isApiSuccess } from "../../utils/apiErrors";
import {
  addModuleFollowup,
  deleteModuleFollowup,
  listModuleFollowups,
} from "../../services/moduleFollowupService";
import MasterSelect from "./MasterSelect";
import {
  FALLBACK_FOLLOWUP_OUTCOME,
  FALLBACK_FOLLOWUP_TYPE,
} from "../../utils/masterFallbacks";

export default function ModuleFollowups({ moduleName, recordId, customerId, props }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    type: "Call",
    outcome: "In Progress",
    notes: "",
    nextFollowupDate: "",
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const load = async () => {
    if (!recordId) return;
    const res = await listModuleFollowups(props, moduleName, recordId);
    setItems(res?.data?.items || []);
  };

  useEffect(() => {
    load();
  }, [moduleName, recordId]);

  const submit = async () => {
    if (!form.notes && !form.nextFollowupDate) {
      setFormError("Add a note or a next follow-up date");
      return;
    }
    setFormError("");
    setSaving(true);
    const res = await addModuleFollowup(props, {
      module: moduleName,
      recordId,
      ...(asEntityId(customerId) ? { customerId: asEntityId(customerId) } : {}),
      type: form.type,
      outcome: form.outcome,
      notes: form.notes,
      nextFollowupDate: form.nextFollowupDate || undefined,
    });
    setSaving(false);
    if (isApiSuccess(res)) {
      setForm({ type: "Call", outcome: "In Progress", notes: "", nextFollowupDate: "" });
      load();
    } else {
      setFormError(getApiErrorMessage(res, "Could not save follow-up"));
    }
  };

  const remove = async (id) => {
    const res = await deleteModuleFollowup(props, id);
    if (res?.code === 200) {
      toastAlert("Follow-up removed", "success");
      load();
    }
  };

  return (
    <Box>
      <div className="drawer-section" style={{ marginBottom: 8 }}>
        <h3 className="drawer-section-title">New follow-up</h3>
        <Box display="grid" gap={8} style={{ gridTemplateColumns: "1fr 1fr" }}>
          <MasterSelect
            group="followup_type"
            fallback={FALLBACK_FOLLOWUP_TYPE}
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
          />
          <MasterSelect
            group="followup_outcome"
            fallback={FALLBACK_FOLLOWUP_OUTCOME}
            value={form.outcome}
            onChange={(e) => setForm({ ...form, outcome: e.target.value })}
          />
          <TextField
            type="date"
            size="small"
            label="Next follow-up"
            InputLabelProps={{ shrink: true }}
            value={form.nextFollowupDate}
            error={Boolean(formError)}
            onChange={(e) => {
              setForm({ ...form, nextFollowupDate: e.target.value });
              if (formError) setFormError("");
            }}
          />
          <TextField
            size="small"
            label="Notes"
            value={form.notes}
            error={Boolean(formError)}
            onChange={(e) => {
              setForm({ ...form, notes: e.target.value });
              if (formError) setFormError("");
            }}
            style={{ gridColumn: "1 / -1" }}
          />
          {formError ? <p className="error-message" style={{ gridColumn: "1 / -1" }}>{formError}</p> : null}
        </Box>
        <Button
          variant="contained"
          disabled={saving}
          onClick={submit}
          className="btn btn-colored"
          style={{ marginTop: 10, marginBottom: 0, textTransform: "none" }}
        >
          Save follow-up
        </Button>
      </div>
      {items.length === 0 && (
        <Typography color="textSecondary" style={{ padding: "8px 4px" }}>
          No follow-ups yet.
        </Typography>
      )}
      {items.map((item) => (
        <div className="followup-item" key={item._id}>
          <div className="drawer-kv" style={{ paddingTop: 0 }}>
            <span className="drawer-kv-label">{item.type}</span>
            <span className="drawer-kv-value">{item.outcome || "—"}</span>
          </div>
          <Typography variant="body2" style={{ color: "#334155", margin: "6px 0 10px" }}>
            {item.notes || "—"}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Next {formatDate(item.nextFollowupDate) || "not set"} · {item.createdBy?.name || "Staff"}
          </Typography>
          <div>
            <Button size="small" color="error" onClick={() => remove(item._id)} style={{ textTransform: "none", paddingLeft: 0 }}>
              Remove
            </Button>
          </div>
        </div>
      ))}
    </Box>
  );
}
